import {
  AutocompleteMultipleSelect,
  Button,
  InfoPanel,
} from "@king-ict/eupisi-ui/components";
import { Col, Modal, Row } from "react-bootstrap";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { OibIdentitySection } from "../../../oib";
import { buildOibPersonDataFromCommitteeEmployee } from "../../utils";
import { DatePickerRange } from "../DatePickerRange";
import { CommitteeEmployee, CommitteeType } from "../../../../models/Committee";
import { getOibErrorMessage } from "../../../../utils/msgUtils";
import { ServiceResultStatus } from "../../../../models";
import { MULTI_SELECT_SINGLE_SELECTION_LIMIT, useCommitteeModal } from ".";

interface IProps {
  committeeType: CommitteeType;
  editingEmployee: CommitteeEmployee | null;
  onSave: (committeeEmployee: CommitteeEmployee) => void;
  onClose: () => void;
}

export const CommitteeModal = ({
  committeeType,
  editingEmployee,
  onSave,
  onClose,
}: IProps) => {
  const {
    filteredEmployees,
    oibEmployeeServiceResult,
    searchOibEmployee,
    employee,
    isEditable,
    editedActiveTo,
    showOibAlert,
    handleEmployeeFilterChange,
    handleOibSystemClick,
    handleEmployeeChangeMultiselect,
    resetModal,
    handleDateOnChange,
    handleSearchOibEmployeeChange,
  } = useCommitteeModal({ committeeType, editingEmployee });

  const { t } = useTranslation();

  const personData = useMemo(
    () =>
      buildOibPersonDataFromCommitteeEmployee({
        employee,
        searchOibEmployee,
        handleSearchOibEmployeeChange,
      }),
    [employee, searchOibEmployee, handleSearchOibEmployeeChange]
  );

  const handleOnClose = () => {
    resetModal();
    onClose();
  };

  const handleOnSave = () => {
    if (!employee) return;

    onSave(employee);
    resetModal();
  };

  return (
    <Modal
      show={!!employee}
      onClose={handleOnClose}
      size="lg"
      backdrop="static"
      centered
      animation={false}
    >
      <Modal.Header>{employee?.committeeRole.name}</Modal.Header>
      <Modal.Body>
        <Row>
          <Col md={9}>
            <AutocompleteMultipleSelect
              label={t("common.expertCommission.employee")}
              name="employeeId"
              value={employee?.employee?.id ? [employee.employee] : []}
              valueProp="id"
              textProp="name"
              onChange={handleEmployeeChangeMultiselect}
              selectOptions={filteredEmployees}
              onFilterChange={handleEmployeeFilterChange}
              maximumSelectedItems={MULTI_SELECT_SINGLE_SELECTION_LIMIT}
            />
          </Col>
        </Row>
        {employee?.committeeRole.checkOIB && (
          <>
            <hr />
            <OibIdentitySection
              showNoOibCheckbox={false}
              isOibConnected={!!employee?.oibEmployee}
              oibValue={
                employee?.oibEmployee?.oib ?? searchOibEmployee.oib ?? ""
              }
              onOibChange={(value) =>
                handleSearchOibEmployeeChange(value, "oib")
              }
              isOibReadonly={!isEditable}
              showConnectButton={isEditable}
              onConnectClick={() => handleOibSystemClick(searchOibEmployee)}
              isConnected={!!employee?.oibEmployee}
              personData={personData}
            />
            {showOibAlert && (
              <Row>
                <Col>
                  <InfoPanel
                    color={
                      oibEmployeeServiceResult?.status !==
                      ServiceResultStatus.Success
                        ? "danger"
                        : "success"
                    }
                    message={
                      oibEmployeeServiceResult?.status
                        ? getOibErrorMessage(oibEmployeeServiceResult?.status)
                        : ""
                    }
                    dismissible={false}
                    fullWidth
                  />
                </Col>
              </Row>
            )}
          </>
        )}

        <br />
        <DatePickerRange
          onChange={handleDateOnChange}
          value={editedActiveTo}
          disabled={!employee || !employee.id}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={handleOnClose} size="sm" outline>
          {t("common.quit")}
        </Button>
        <Button onClick={handleOnSave} size="sm">
          {t("common.save")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
